/**
 * iter350 — « Mon poste » : les 3 rubriques personnelles du collaborateur.
 * À faire / En travaux / Terminé — avancement individuel, sans aucun prix.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Wrench, CheckCircle2, ClipboardList, ChevronRight, Hand, CalendarDays, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import NouveauDossierDialog from "@/components/atelier/NouveauDossierDialog";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const RUBRIQUES = [
  { key: "a_faire", label: "À faire", icon: ClipboardList, tone: "bg-amber-50 text-amber-800 border-amber-200" },
  { key: "en_travaux", label: "En travaux", icon: Wrench, tone: "bg-[#52a675]/10 text-[#2d5f3f] border-[#52a675]/30" },
  { key: "termine", label: "Terminé", icon: CheckCircle2, tone: "bg-gray-100 text-gray-700 border-gray-200" },
];

const ATTENTE_TONE = {
  devis: "bg-orange-50 border-orange-300",
  pieces: "bg-orange-50 border-orange-300",
  validation: "bg-orange-50 border-orange-300",
  accord_client: "bg-blue-50 border-blue-300",
};

const libelleJour = (iso) => {
  const s = new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CH", {
    weekday: "long", day: "numeric", month: "long",
  });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export default function MonPoste() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("a_faire");
  const [loading, setLoading] = useState(true);
  const [agenda, setAgenda] = useState([]);
  const [prochains, setProchains] = useState([]);
  const [nouveauOpen, setNouveauOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/atelier/mon-poste");
      setData(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/atelier/agenda", { params: { jours: 7 } })
      .then(({ data }) => { setAgenda(data.items || []); setProchains(data.prochains || []); })
      .catch(() => { setAgenda([]); setProchains([]); });
  }, []);

  const receptionner = async (rdv) => {
    try {
      const { data } = await api.post(`/atelier/reception/${rdv.id}`, {});
      toast.success(
        data.pris ? `Dossier ${data.numero} pris en charge`
          : data.cree ? `Dossier ${data.numero} créé` : `Dossier ${data.numero} ouvert`,
      );
      navigate(`/atelier/travail/${data.or_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Réception impossible");
    }
  };

  const ouvrir = async (or, libre = false) => {
    // iter357 — Celui qui ouvre un dossier que personne n'a pris se l'attribue.
    if (libre && !isAdmin) {
      try {
        await api.post(`/atelier/travail/${or.id}/prendre`, {});
        toast.success(`Dossier ${or.numero} pris en charge`);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Attribution impossible");
      }
    }
    navigate(`/atelier/travail/${or.id}`);
  };

  const carte = (or, libre = false) => (
    <button
      key={or.id}
      onClick={() => ouvrir(or, libre)}
      className={`w-full text-left p-3.5 rounded-xl border-2 bg-white hover:border-[#52a675] transition-colors flex items-center gap-3 ${
        or.attente ? ATTENTE_TONE[or.attente.type] || "border-gray-200" : "border-gray-200"
      }`}
      data-testid={`poste-carte-${or.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono font-bold text-sm">{or.numero}</span>
          {or.non_lus > 0 && (
            <span
              className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
              data-testid={`poste-non-lus-${or.id}`}
            >
              {or.non_lus}
            </span>
          )}
          {or.sous_statut && (
            <Badge
              variant="outline"
              className={`text-[10px] ${or.sous_statut.ton === "attente"
                ? "bg-orange-50 text-orange-800 border-orange-300"
                : "bg-[#52a675]/10 text-[#2d5f3f] border-[#52a675]/30"}`}
              data-testid={`poste-attente-${or.id}`}
            >
              {or.sous_statut.label}
            </Badge>
          )}
        </div>
        <div className="text-sm text-gray-900 truncate">{or.vehicule_label || "Véhicule non renseigné"}</div>
        {or.notes_client && (
          <div className="text-xs text-gray-500 truncate mt-0.5">{or.notes_client}</div>
        )}
      </div>
      <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
    </button>
  );

  if (loading) {
    return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#52a675]" /></div>;
  }

  const listes = { ...data, libres: data?.libres || [] };

  return (
    <div className="p-3 sm:p-6 max-w-3xl mx-auto" data-testid="mon-poste">
      <div className="flex items-center gap-2 mb-4">
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 flex items-center gap-2 flex-1">
          <Wrench className="w-6 h-6 text-[#52a675]" /> Mon poste
        </h1>
        <Button
          onClick={() => setNouveauOpen(true)}
          className="h-10 bg-[#52a675] hover:bg-[#2d5f3f]"
          data-testid="poste-nouveau-dossier"
        >
          <Plus className="w-4 h-4 mr-1" /> Dossier
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-1.5 mb-4">
        {RUBRIQUES.map((r) => {
          const Icon = r.icon;
          const n = (listes[r.key] || []).length;
          const actif = tab === r.key;
          return (
            <button
              key={r.key}
              onClick={() => setTab(r.key)}
              className={`py-2.5 px-2 rounded-xl border-2 transition-colors flex flex-col items-center gap-0.5 ${
                actif ? r.tone : "bg-white border-gray-200 text-gray-500"
              }`}
              data-testid={`poste-tab-${r.key}`}
            >
              <Icon className="w-4 h-4" />
              <span className="text-[11px] sm:text-xs font-semibold leading-tight text-center">{r.label}</span>
              <span className="text-base font-bold leading-none" data-testid={`poste-count-${r.key}`}>{n}</span>
            </button>
          );
        })}
      </div>

      <div className="space-y-2" data-testid={`poste-liste-${tab}`}>
        {(listes[tab] || []).length === 0 && (
          <Card className="p-8 text-center text-sm text-gray-400" data-testid="poste-vide">
            Rien dans « {RUBRIQUES.find((r) => r.key === tab)?.label} »
          </Card>
        )}
        {(listes[tab] || []).map(carte)}
      </div>

      {tab === "a_faire" && (
        <div className="mt-6 space-y-4">
          {/* iter355 — Le collaborateur réceptionne lui-même un véhicule attendu */}
          <div data-testid="poste-agenda">
            <div className="text-[11px] uppercase tracking-wider text-gray-500 flex items-center gap-1.5 mb-2">
              <CalendarDays className="w-3.5 h-3.5" /> Rendez-vous du jour
            </div>
            {agenda.length === 0 && (
              <Card className="p-4 text-center text-xs text-gray-400">Aucun rendez-vous aujourd'hui</Card>
            )}
            <div className="space-y-2">
              {agenda.map((r) => (
                <div
                  key={r.id}
                  className="p-3 rounded-xl border-2 border-blue-100 bg-white flex items-center gap-2"
                  data-testid={`poste-rdv-${r.id}`}
                >
                  <span className="font-mono text-xs font-bold w-12">{r.heure_debut || "—"}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{r.vehicule_label || r.client_nom}</div>
                    <div className="text-[11px] text-gray-500 truncate">{r.type_intervention_nom || r.client_nom}</div>
                  </div>
                  <Button
                    size="sm" variant={r.or_id ? "outline" : "default"}
                    className={r.or_id ? "h-9 border-gray-300" : "h-9 bg-[#52a675] hover:bg-[#2d5f3f]"}
                    onClick={() => receptionner(r)}
                    data-testid={`poste-receptionner-${r.id}`}
                  >
                    {r.or_id ? "Ouvrir" : "Réceptionner"}
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* iter359 — Aperçu des prochains jours (lecture seule) */}
          {prochains.length > 0 && (
            <div data-testid="poste-prochains">
              <div className="text-[11px] uppercase tracking-wider text-gray-500 flex items-center gap-1.5 mb-2">
                <CalendarDays className="w-3.5 h-3.5" /> Prochains jours ({prochains.length})
              </div>
              <div className="space-y-3">
                {Object.entries(
                  prochains.reduce((acc, r) => {
                    (acc[r.date] = acc[r.date] || []).push(r);
                    return acc;
                  }, {}),
                ).map(([jour, rdvs]) => (
                  <div key={jour} data-testid={`poste-jour-${jour}`}>
                    <div className="text-xs font-semibold text-gray-700 mb-1">{libelleJour(jour)}</div>
                    <div className="space-y-1.5">
                      {rdvs.map((r) => (
                        <div
                          key={r.id}
                          className="p-2.5 rounded-lg border border-gray-200 bg-white flex items-center gap-2"
                          data-testid={`poste-prochain-${r.id}`}
                        >
                          <span className="font-mono text-xs font-bold w-12 text-gray-600">{r.heure_debut || "—"}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm truncate">{r.vehicule_label || r.client_nom}</div>
                            <div className="text-[11px] text-gray-500 truncate">
                              {r.type_intervention_nom || r.client_nom}
                            </div>
                          </div>
                          {r.or_numero && (
                            <span className="font-mono text-[10px] text-gray-400">{r.or_numero}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {listes.libres.length > 0 && (
            <div data-testid="poste-libres">
              <div className="text-[11px] uppercase tracking-wider text-gray-500 flex items-center gap-1.5 mb-2">
                <Hand className="w-3.5 h-3.5" /> Dossiers que personne n'a pris ({listes.libres.length})
              </div>
              <div className="space-y-2">{listes.libres.map((or) => carte(or, true))}</div>
            </div>
          )}
        </div>
      )}

      <NouveauDossierDialog open={nouveauOpen} onClose={() => setNouveauOpen(false)} />
    </div>
  );
}
